import { ErrorBoundary } from "@/components/app/ErrorBoundary"
import Button from "@/components/button/Button"
import { useAuth } from "@/components/context/AuthProvider"
import TablePlaceholder from "@/components/table-placeholder/TablePlaceholder"
import { setAuthToken } from "@/helpers/auth"
import { websiteRoutes } from "@/router/routes"
import { Col, Row, Space } from "antd"
import { FC } from "react"
import { useNavigate, useRouteError } from "react-router"

/** Detects chunk loading errors (e.g. after a new deployment when old chunks are gone). */
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false

  const toLower = (s: string) => s.toLowerCase()

  if (error instanceof Error) {
    const text = [error.name, error.message, error.stack ?? "", String(error)].map(toLower).join(" ")
    return (
      text.includes("failed to fetch dynamically imported module") ||
      text.includes("loading chunk") ||
      text.includes("chunk load failed") ||
      (text.includes("dynamically imported") && text.includes("/assets/"))
    )
  }

  if (error && typeof error === "object") {
    if ("status" in error) {
      const status = (error as { status?: number }).status
      if (status === 404 || status === 0) return true
    }
    if ("message" in error) {
      const msg = toLower(String((error as { message?: unknown }).message ?? ""))
      if (
        msg.includes("failed to fetch dynamically imported module") ||
        msg.includes("loading chunk") ||
        msg.includes("chunk load failed") ||
        (msg.includes("dynamically imported") && msg.includes("/assets/"))
      ) {
        return true
      }
    }
  }

  const str = toLower(String(error))
  return (
    str.includes("failed to fetch dynamically imported module") ||
    str.includes("loading chunk") ||
    str.includes("chunk load failed") ||
    (str.includes("dynamically imported") && str.includes("/assets/"))
  )
}

const ErrorPage: FC = () => {
  const { organization } = useAuth()
  const navigate = useNavigate()
  const { website } = websiteRoutes
  const error = useRouteError()
  const chunkError = isChunkLoadError(error)

  const onReload = () => {
    window.location.reload()
  }

  const onHomePage = () => {
    navigate(`/${organization}${website.home}`)
    localStorage.clear()
    setAuthToken("")
  }

  const title = chunkError ? "New version available" : "Something went wrong"

  const text = chunkError
    ? 'A new version of the application was deployed. Please click "Reload the page" to get the latest version.'
    : "An unexpected error has occurred. We are sorry for the disruption. Please reload the page by clicking the button below."

  return (
    <ErrorBoundary>
      <Row style={{ paddingTop: 100 }} justify={"center"}>
        <Col span={12}>
          <Row justify={"center"}>
            <Col span={12}>
              <TablePlaceholder title={title} text={text} />
            </Col>
          </Row>
          <Row justify={"center"}>
            <Space>
              <Button btnType={"ghost"} text={"Go to Home page"} onClick={onHomePage} />
              <Button text={"Reload the page"} onClick={onReload} />
            </Space>
          </Row>
        </Col>
      </Row>
    </ErrorBoundary>
  )
}

export default ErrorPage
